// API client. One place that knows about the token, so every fetch, the room
// WebSocket and every <audio src> agree on how it travels.
//
// Two surfaces share this client. The ADMIN app sends the shared admin token
// and names a channel. The ACCOUNT app (its own build and worker, one
// deployment per channel) sends a producer's own Seedbed session to
// /api/account, which mirrors the admin paths it stands in for and names no
// channel — the deployment IS one — so pages built on `api()` and
// `objectUrl()`, the play screen first of all, work on either without knowing
// which they are on. The surface is fixed at boot (main.tsx).

import { accessToken, refreshSession } from "@web-shared/lib/account";
import type {
  IntentEnvelope,
  IntentResponse,
  RoomStateResponse,
} from "@shared/sync/intents.js";

const TOKEN_KEY = "seedbed-viz.token";
const CHANNEL_KEY = "seedbed-viz.channel";
const CHANNELS_KEY = "seedbed-viz.channels";

/**
 * Which backend to read. `stable` and `beta` are separate deployments with
 * separate databases and separate accounts, so this is not a display option —
 * it decides which seeds and jams exist at all.
 *
 * Rides the query string rather than a header because it has to work for the
 * WebSocket and for `<audio src>` too, neither of which can set headers.
 */
export type Channel = "stable" | "beta";

export function getChannel(): Channel {
  return localStorage.getItem(CHANNEL_KEY) === "beta" ? "beta" : "stable";
}

export function setChannel(channel: Channel): void {
  localStorage.setItem(CHANNEL_KEY, channel);
}

/**
 * Which channels THIS deployment can serve, as reported by `/api/session`.
 *
 * Stable and beta live on different Cloudflare accounts, and every binding that
 * reaches them is account-local, so a deployment carries one or the other — not
 * both. Cached in localStorage only to keep the first paint from flickering the
 * switch; `/api/session` rewrites it on every boot, so a deployment that gains
 * or loses a channel is reflected one load later, not never.
 */
export function getAvailableChannels(): Channel[] {
  const raw = localStorage.getItem(CHANNELS_KEY);
  if (!raw) return ["stable", "beta"];
  try {
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return ["stable", "beta"];
    const known = parsed.filter((c): c is Channel => c === "stable" || c === "beta");
    return known.length > 0 ? known : ["stable", "beta"];
  } catch {
    // Hand-edited or half-written storage. Both channels is the honest guess:
    // the next /api/session replaces it either way.
    return ["stable", "beta"];
  }
}

/**
 * Record what the server reports and, if the stored channel is not among them,
 * move to one that is. Returns true when the channel changed — the caller
 * reloads, because a switch has to take every in-flight cache with it.
 *
 * Deliberately NOT a silent read-through: the channel the UI names is always
 * the channel the data came from. All this does is stop the app defaulting to a
 * channel this deployment could never answer.
 */
export function reconcileChannels(channels: Channel[]): boolean {
  const fallback = channels[0];
  if (!fallback) return false;
  localStorage.setItem(CHANNELS_KEY, JSON.stringify(channels));
  if (channels.includes(getChannel())) return false;
  setChannel(fallback);
  return true;
}

export type Surface = "admin" | "account";

let surface: Surface = "admin";

export function setSurface(next: Surface): void {
  surface = next;
}

export function getSurface(): Surface {
  return surface;
}

export function getToken(): string {
  return localStorage.getItem(TOKEN_KEY) ?? "";
}

export function setToken(token: string): void {
  localStorage.setItem(TOKEN_KEY, token);
}

export function clearToken(): void {
  localStorage.removeItem(TOKEN_KEY);
}

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

/** Append the active channel without disturbing a path's existing query. */
function withChannel(path: string): string {
  return `${path}${path.includes("?") ? "&" : "?"}channel=${getChannel()}`;
}

/** Where a path goes on the active surface. The account API has no channel. */
function surfaceUrl(path: string): string {
  return surface === "account" ? `/api/account${path}` : `/api${withChannel(path)}`;
}

function surfaceToken(): string {
  return surface === "account" ? accessToken() : getToken();
}

export async function api<T>(path: string, init?: RequestInit): Promise<T> {
  const send = () =>
    fetch(surfaceUrl(path), {
      ...init,
      headers: { ...init?.headers, Authorization: `Bearer ${surfaceToken()}` },
    });
  let res = await send();
  // An account's access token lives an hour; the refresh token behind it a
  // month. One refresh and one retry — a second 401 is a real sign-out.
  if (res.status === 401 && surface === "account" && (await refreshSession())) {
    res = await send();
  }
  if (!res.ok) {
    const body = (await res.json().catch(() => null)) as { error?: string } | null;
    throw new ApiError(res.status, body?.error ?? `Request failed (${res.status})`);
  }
  return (await res.json()) as T;
}

/**
 * URLs consumed by the browser itself — <audio src>, download links, the
 * WebSocket — can't carry an Authorization header, so the token rides the query
 * string. Same secret, same check on the server; only the channel differs.
 */
export function tokenizedUrl(path: string, params: Record<string, string> = {}): string {
  const base = surface === "account" ? `/api/account${path}` : `/api${path}`;
  const url = new URL(base, window.location.origin);
  for (const [k, v] of Object.entries(params)) url.searchParams.set(k, v);
  url.searchParams.set("token", surfaceToken());
  if (surface === "admin") url.searchParams.set("channel", getChannel());
  return url.toString();
}

/** `edit` joins the roster as a participant instead of a hidden observer. */
export function roomSocketUrl(jamId: string, edit = false): string {
  const url = new URL(
    tokenizedUrl(`/rooms/${encodeURIComponent(jamId)}/ws`, edit ? { edit: "1" } : {}),
  );
  url.protocol = url.protocol === "https:" ? "wss:" : "ws:";
  return url.toString();
}

/** The room's whole truth — full ClipData per clip, plus presigned audio URLs. */
export function roomState(jamId: string): Promise<RoomStateResponse> {
  return api<RoomStateResponse>(`/rooms/${encodeURIComponent(jamId)}/state`);
}

/**
 * Propose changes. Resolves with the room's verdict: what it accepted and what
 * it refused, each rejection carrying the value the room actually holds.
 */
export function postIntents(
  jamId: string,
  envelope: IntentEnvelope,
): Promise<IntentResponse> {
  return api<IntentResponse>(`/rooms/${encodeURIComponent(jamId)}/intents`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(envelope),
  });
}

/** What `POST /rooms/:jamId/audio` answers: the pool key the bytes live under. */
export interface RoomAudioUpload {
  key: string;
  hash: string;
  sizeBytes: number;
  /** The pool already held these exact bytes — nothing was written. */
  existed: boolean;
}

/** Put WAV bytes into the room's audio pool. The key comes back ready to name
 * on an `upsertClip` create. */
export function uploadRoomAudio(jamId: string, bytes: ArrayBuffer): Promise<RoomAudioUpload> {
  return api<RoomAudioUpload>(`/rooms/${encodeURIComponent(jamId)}/audio`, {
    method: "POST",
    headers: { "Content-Type": "audio/wav" },
    body: bytes,
  });
}

export function objectUrl(key: string, download = false): string {
  return tokenizedUrl("/r2/object", download ? { key, download: "1" } : { key });
}

/** A pool object or its preview: `<prefix>blobs/<sha256>.wav|.mp3` — named by
 * its bytes, so the same key is the same bytes forever. */
const POOL_KEY = /(^|\/)blobs\/[0-9a-f]{64}\.(wav|mp3)$/;

/**
 * `fetch` for audio the page decodes itself — the player's objects and
 * previews. An `objectUrl` for a POOL object is fetched without its token,
 * which goes in the Authorization header instead: the token rotates hourly,
 * and in the URL it made every rotation a new URL the browser cache had
 * never seen, so a room downloaded its stems again on every visit. Without
 * it, the URL is as stable as the key and the server's `immutable` holds.
 * One refresh and one retry on a 401, as `api` does. Anything else is a
 * plain fetch.
 */
export async function audioFetch(url: string, init?: RequestInit): Promise<Response> {
  // No page (a test, a worker): nothing to authorize against.
  const origin = typeof window === "undefined" ? undefined : window.location?.origin;
  if (!origin) return init ? fetch(url, init) : fetch(url);
  const parsed = new URL(url, origin);
  const token = parsed.searchParams.get("token");
  const key = parsed.searchParams.get("key");
  if (parsed.origin !== origin || !token || !key || !POOL_KEY.test(key)) return init ? fetch(url, init) : fetch(url);
  parsed.searchParams.delete("token");
  const stable = parsed.toString();
  const send = (bearer: string) =>
    fetch(stable, { ...init, headers: { ...(init?.headers as Record<string, string> | undefined), Authorization: `Bearer ${bearer}` } });
  // The freshest token, not the one baked into the URL when it was built.
  let res = await send(surfaceToken() || token);
  if (res.status === 401 && surface === "account" && (await refreshSession())) res = await send(surfaceToken());
  return res;
}
